import { ModalDialog } from 'components/ModalDialog';
import { TooltipWrapper } from 'components/TooltipWrapper';
import type {
    ContractOtherCostLimitData,
    UercCalculationOtherCostLimitData,
    UercCalculationSeasonData,
} from 'gql/graphql';
import { Button, GridItem, IconEye, IconQuestion, Informer, Text } from 'uikit';
import { formatNumberToRu } from 'utils';

import { useCallback, useMemo, useState } from 'react';

import Styles from './otherManualSeasonDistribution.module.css';

export const OtherManualSeasonDistributionView = ({
    limits,
    costRounding,
    constructionTypeName,
    constructionTypeCost,
    seasons,
}: {
    limits: Partial<ContractOtherCostLimitData | UercCalculationOtherCostLimitData>[] | undefined;
    costRounding: number;
    constructionTypeCost: string | null | undefined;
    constructionTypeName: string;
    seasons?: UercCalculationSeasonData[] | null;
}) => {
    const [isOpened, setIsOpened] = useState(false);

    const toggleModalHandler = useCallback(() => {
        setIsOpened((o) => !o);
    }, []);

    const currentConstructionTypeSeasonsSum = useMemo(
        () => limits?.reduce((sumAcc, limit) => sumAcc + limit.cost, 0),
        [limits],
    );

    return (
        <>
            <Button
                view="secondary"
                type="button"
                iconLeft={IconEye}
                iconSize="xs"
                size="s"
                onClick={toggleModalHandler}
                className={Styles.manualSeasonDistributionViewBtn}
            />

            <ModalDialog
                isOpen={isOpened}
                title="Способ распределения"
                bodyWidth={620}
                onClose={toggleModalHandler}
                isOnClickOutsideDisable
                withTitleCloseButton
                btnCancelLabel="Закрыть"
                btnCancelView="primary"
            >
                <div className={Styles.viewBody}>
                    <Informer
                        status="system"
                        className={Styles.distributionSumInformer}
                        label={
                            <div className={Styles.distributionSumInformerBody}>
                                <Text view="secondary">{constructionTypeName}</Text>

                                <div className={Styles.distributionSum}>
                                    <Text weight="semibold" view="primary">
                                        {formatNumberToRu(currentConstructionTypeSeasonsSum, costRounding)} ₽ /&nbsp;
                                    </Text>
                                    <Text view="secondary">
                                        {formatNumberToRu(constructionTypeCost, costRounding)} ₽
                                    </Text>
                                    <TooltipWrapper
                                        message="Сумма лимитов/Лимит по Договору"
                                        className={Styles.distributionQuestion}
                                        toolTipClassName={Styles.distributionQuestionTooltip}
                                        isInteractive={false}
                                    >
                                        <IconQuestion size="s" view="secondary" />
                                    </TooltipWrapper>
                                </div>
                            </div>
                        }
                    />

                    <GridItem className={Styles.titles}>
                        <Text view="secondary">Сезон</Text>
                        <Text view="secondary">Сумма, руб. без НДС</Text>
                    </GridItem>

                    {limits?.map((limit) => (
                        <GridItem
                            key={
                                'season' in limit
                                    ? limit?.season?.id
                                    : (limit as UercCalculationOtherCostLimitData).seasonId
                            }
                            className={Styles.valueView}
                        >
                            <Text className={Styles.valueLabel} view="secondary">
                                {'season' in limit
                                    ? limit?.season?.year
                                    : seasons?.find(
                                          (season) =>
                                              season.seasonId === (limit as UercCalculationOtherCostLimitData).seasonId,
                                      )?.year}
                            </Text>
                            <Text>{formatNumberToRu(limit?.cost, costRounding ?? 0)}</Text>
                        </GridItem>
                    ))}
                </div>
            </ModalDialog>
        </>
    );
};

OtherManualSeasonDistributionView.displayName = 'OtherManualSeasonDistributionView';
